/**
 * Состояние и расчёт процента/подписи для NDJSON-замера скорости (админка).
 * Процент — от доли переданных байт (скачано + отдано) к сумме плановых объёмов (с бэкенда),
 * чтобы полоска не уменьшалась при просадке мгновенной скорости (Мб/с).
 */
export function createProbeProgressContext() {
  return {
    lastPct: 0,
    lastLabel: 'Подготовка…',
    downloadBytesPlanned: 0,
    uploadBytesPlanned: 0,
    downloadBytesDone: 0,
    uploadBytesDone: 0,
    /** если бэкенд без планов — монотонная оценка по фазам (без уменьшения) */
    legacyFloor: 0,
  };
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/**
 * @param {object} ev — событие из потока (event, phase, mbps, bytes, error, …)
 * @param {ReturnType<typeof createProbeProgressContext>} ctx
 * @returns {{ percent: number; label: string } | null}
 */
export function reduceProbeProgressEvent(ev, ctx) {
  if (!ev || typeof ev.event !== 'string') {
    return null;
  }

  let pct = ctx.lastPct;
  let label = ctx.lastLabel;
  const hasBytePlan =
    ctx.downloadBytesPlanned + ctx.uploadBytesPlanned > 0;

  const applyBytePercent = () => {
    if (!hasBytePlan) return;
    const planned = ctx.downloadBytesPlanned + ctx.uploadBytesPlanned;
    const dl = Math.min(ctx.downloadBytesDone, ctx.downloadBytesPlanned);
    const ul = Math.min(ctx.uploadBytesDone, ctx.uploadBytesPlanned);
    const next = (100 * (dl + ul)) / planned;
    pct = Math.min(100, Math.max(pct, next));
  };

  switch (ev.event) {
    case 'start':
      ctx.downloadBytesDone = 0;
      ctx.uploadBytesDone = 0;
      ctx.downloadBytesPlanned = safeNum(ev.download_bytes_planned);
      ctx.uploadBytesPlanned = safeNum(ev.upload_bytes_planned);
      ctx.legacyFloor = 0;
      pct = 0;
      label = 'Подключение…';
      break;
    case 'phase':
      if (ev.phase === 'download') {
        label = 'Загрузка данных…';
        if (!hasBytePlan) {
          ctx.legacyFloor = Math.max(ctx.legacyFloor, 8);
          pct = Math.max(pct, ctx.legacyFloor);
        }
      } else if (ev.phase === 'upload') {
        label = 'Передача данных…';
        if (!hasBytePlan) {
          ctx.legacyFloor = Math.max(ctx.legacyFloor, 52);
          pct = Math.max(pct, ctx.legacyFloor);
        }
      }
      applyBytePercent();
      break;
    case 'progress':
      if (ev.phase === 'ip') {
        label = 'Получение IP…';
        if (!hasBytePlan) {
          ctx.legacyFloor = Math.max(ctx.legacyFloor, 4);
          pct = Math.max(pct, ctx.legacyFloor);
        }
      } else if (ev.phase === 'download') {
        ctx.downloadBytesDone = Math.max(ctx.downloadBytesDone, safeNum(ev.bytes));
        const mbps = typeof ev.mbps === 'number' ? ev.mbps : 0;
        const dlMb = ctx.downloadBytesDone / (1024 * 1024);
        const planMb = ctx.downloadBytesPlanned / (1024 * 1024);
        if (hasBytePlan && planMb > 0) {
          label = `Скачано ${dlMb.toFixed(1)} из ${planMb.toFixed(1)} МБ · ${mbps.toFixed(1)} Мбит/с`;
        } else {
          label = `Загрузка ${mbps.toFixed(1)} Мбит/с`;
          const m = mbps;
          const legacy = 10 + Math.min(40, (m / 90) * 40);
          ctx.legacyFloor = Math.max(ctx.legacyFloor, legacy);
          pct = Math.max(pct, ctx.legacyFloor);
        }
        applyBytePercent();
      } else if (ev.phase === 'upload') {
        ctx.uploadBytesDone = Math.max(ctx.uploadBytesDone, safeNum(ev.bytes));
        const mbps = typeof ev.mbps === 'number' ? ev.mbps : 0;
        const ulMb = ctx.uploadBytesDone / (1024 * 1024);
        const planMb = ctx.uploadBytesPlanned / (1024 * 1024);
        if (hasBytePlan && planMb > 0) {
          label = `Передано ${ulMb.toFixed(1)} из ${planMb.toFixed(1)} МБ · ${mbps.toFixed(1)} Мбит/с`;
        } else {
          label = `Отдача ${mbps.toFixed(1)} Мбит/с`;
          const m = mbps;
          const legacy = 52 + Math.min(44, (m / 90) * 44);
          ctx.legacyFloor = Math.max(ctx.legacyFloor, legacy);
          pct = Math.max(pct, ctx.legacyFloor);
        }
        applyBytePercent();
      }
      break;
    case 'done':
      pct = 100;
      label = ev.error ? `Ошибка: ${ev.error}` : 'Готово';
      break;
    default:
      return null;
  }

  ctx.lastPct = Math.min(100, Math.max(0, Math.round(pct)));
  ctx.lastLabel = label;
  return { percent: ctx.lastPct, label: ctx.lastLabel };
}
